import React, { useMemo } from 'react';
import { Compass, Wind, MoveUp, Activity } from 'lucide-react';

/**
 * CockpitView — a stylized "out the cockpit window" preview rendered with
 * pure CSS + SVG. No external 3D libraries, so it stays light on mobile.
 *
 * What it visualises:
 *   • Sky gradient that darkens with altitude (0m = light blue, 12000m+ = stratosphere)
 *   • Curved Earth horizon line — the higher you fly the more curve
 *   • Heading compass strip at the top
 *   • HUD readouts (altitude, speed, heading, ETA, position)
 *   • A small sun/moon based on the local time at the aircraft's longitude
 */
const COMPASS_LABELS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

const headingToCardinal = (heading) => {
  if (!Number.isFinite(heading)) return '—';
  const idx = Math.round(((heading % 360) / 45)) % 8;
  return COMPASS_LABELS[idx];
};

const localHourFromLng = (lng) => {
  // Rough local time at the aircraft's current longitude
  const utc = new Date();
  const offsetH = lng / 15;
  return (utc.getUTCHours() + offsetH + 24) % 24;
};

const skyColors = (altitudeM, hour) => {
  // Altitude tint: at 12km we're well into the upper troposphere, sky goes
  // dramatically darker (Concorde / U-2 effect).
  const alt = Math.max(0, Math.min(15000, altitudeM || 0));
  const altFactor = alt / 15000; // 0 .. 1
  const isNight = hour < 6 || hour > 19;
  if (isNight) {
    // Night palette: indigo to near-black
    const top = `rgb(${Math.round(10 - 8 * altFactor)}, ${Math.round(12 - 8 * altFactor)}, ${Math.round(40 - 30 * altFactor)})`;
    const horizon = `rgb(${Math.round(40 + 30 * (1 - altFactor))}, ${Math.round(30 + 20 * (1 - altFactor))}, ${Math.round(80 + 30 * (1 - altFactor))})`;
    const ground = '#0a0e1f';
    return { top, horizon, ground, isNight: true };
  }
  // Day palette: deep blue at top, lighter blue at horizon
  const top = `rgb(${Math.round(135 - 100 * altFactor)}, ${Math.round(180 - 120 * altFactor)}, ${Math.round(235 - 80 * altFactor)})`;
  const horizon = `rgb(${Math.round(220 - 60 * altFactor)}, ${Math.round(230 - 30 * altFactor)}, ${Math.round(245 - 10 * altFactor)})`;
  const ground = altFactor > 0.7 ? '#2c3e50' : '#3d5a40';
  return { top, horizon, ground, isNight: false };
};

export const CockpitView = ({ heading = 0, altitudeM = 0, speedKmh = 0, lat, lng, etaLabel, depIata, arrIata, status }) => {
  const hour = useMemo(() => (Number.isFinite(lng) ? localHourFromLng(lng) : new Date().getUTCHours()), [lng]);
  const sky = useMemo(() => skyColors(altitudeM, hour), [altitudeM, hour]);
  const cardinal = headingToCardinal(heading);
  // Curvature: the higher we go, the more pronounced the horizon arc
  const horizonY = 60 - Math.min(20, (altitudeM || 0) / 600); // % from top
  const arcDepth = Math.min(8, (altitudeM || 0) / 1500);     // px

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-800 shadow-lg bg-slate-900" data-testid="cockpit-view">
      {/* Compass strip */}
      <div className="bg-slate-900/95 text-emerald-300 border-b border-emerald-500/30 px-3 py-2 flex items-center justify-between text-[11px] font-mono uppercase tracking-[0.3em]">
        <div className="flex items-center gap-2">
          <Compass className="h-3.5 w-3.5" />
          <span>HDG <b className="text-emerald-200">{Number.isFinite(heading) ? Math.round(heading).toString().padStart(3, '0') : '---'}°</b> · {cardinal}</span>
        </div>
        <div className="flex items-center gap-3">
          <span>ALT <b className="text-emerald-200">{(altitudeM || 0).toLocaleString()} m</b></span>
          <span>SPD <b className="text-emerald-200">{Math.round(speedKmh || 0)} km/h</b></span>
        </div>
      </div>

      {/* The window itself */}
      <div className="relative h-[220px] sm:h-[280px] overflow-hidden">
        {/* Sky */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, ${sky.top} 0%, ${sky.horizon} ${horizonY}%, ${sky.ground} ${horizonY + 0.5}%, ${sky.ground} 100%)`,
          }}
        />
        {/* Curved horizon overlay */}
        <svg
          className="absolute inset-0 w-full h-full pointer-events-none"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path
            d={`M 0 ${horizonY} Q 50 ${horizonY - arcDepth / 4} 100 ${horizonY}`}
            stroke="rgba(255,255,255,0.4)"
            strokeWidth="0.3"
            fill="none"
          />
        </svg>

        {/* Sun / moon */}
        <div
          className={`absolute rounded-full ${sky.isNight ? 'bg-slate-100 shadow-[0_0_30px_rgba(220,230,255,0.4)]' : 'bg-yellow-200 shadow-[0_0_40px_rgba(255,230,140,0.6)]'}`}
          style={{
            width: 36,
            height: 36,
            top: `${horizonY - 12}%`,
            left: `${(((heading || 0) % 360) / 360) * 100}%`,
            transform: 'translate(-50%, -50%)',
          }}
          aria-hidden
        />

        {/* Star field at high altitude */}
        {altitudeM > 9000 && Array.from({ length: 16 }).map((_, i) => (
          <div
            key={i}
            className="absolute bg-white rounded-full opacity-70"
            style={{
              width: 2,
              height: 2,
              top: `${(i * 7) % (horizonY - 6)}%`,
              left: `${(i * 13.7) % 100}%`,
            }}
          />
        ))}

        {/* Wing tip at the bottom-right corner — adds depth, suggests POV */}
        <svg
          className="absolute bottom-0 right-0 pointer-events-none"
          width="200"
          height="120"
          viewBox="0 0 200 120"
          aria-hidden
        >
          <polygon points="0,120 200,120 200,40" fill="#1f2937" opacity="0.85" />
          <line x1="0" y1="120" x2="200" y2="40" stroke="#10b981" strokeWidth="2" opacity="0.6" />
          <circle cx="180" cy="48" r="4" fill="#10b981" className="animate-pulse" />
        </svg>

        {/* HUD readouts */}
        <div className="absolute top-3 left-3 right-3 flex justify-between text-[10px] font-mono text-emerald-300 pointer-events-none">
          <div className="bg-black/40 backdrop-blur-sm rounded px-2 py-1 border border-emerald-500/30">
            <div className="text-[9px] opacity-70">FLIGHT</div>
            <div className="font-bold tracking-wider">{depIata || '—'} → {arrIata || '—'}</div>
          </div>
          <div className="bg-black/40 backdrop-blur-sm rounded px-2 py-1 border border-emerald-500/30 text-right">
            <div className="text-[9px] opacity-70">STATUS</div>
            <div className="font-bold tracking-wider uppercase">{status || '—'}</div>
          </div>
        </div>

        <div className="absolute bottom-3 left-3 right-3 flex justify-between text-[10px] font-mono text-emerald-300 pointer-events-none">
          <div className="bg-black/40 backdrop-blur-sm rounded px-2 py-1 border border-emerald-500/30">
            <div className="text-[9px] opacity-70">POSITION</div>
            <div className="font-bold tracking-wider">
              {Number.isFinite(lat) ? `${lat.toFixed(2)}°` : '—'}, {Number.isFinite(lng) ? `${lng.toFixed(2)}°` : '—'}
            </div>
          </div>
          {etaLabel && (
            <div className="bg-emerald-600/80 backdrop-blur-sm rounded px-3 py-1 border border-emerald-300/60">
              <div className="text-[9px] opacity-90 text-white">TIME TO LANDING</div>
              <div className="font-bold tracking-wider text-white">{etaLabel}</div>
            </div>
          )}
        </div>

        {/* Centre crosshair */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-emerald-400 opacity-60 pointer-events-none">
          <div className="w-12 h-px bg-current" />
          <div className="h-12 w-px bg-current absolute -top-6 left-6" />
        </div>
      </div>

      {/* Footer */}
      <div className="bg-slate-900 text-slate-400 px-3 py-2 text-[10px] font-mono flex items-center gap-3 border-t border-slate-800">
        <span className="flex items-center gap-1"><MoveUp className="h-3 w-3" /> {(altitudeM || 0).toLocaleString()} m</span>
        <span className="flex items-center gap-1"><Wind className="h-3 w-3" /> {Math.round(speedKmh || 0)} km/h</span>
        <span className="flex items-center gap-1"><Activity className="h-3 w-3" /> {sky.isNight ? 'Night flight' : 'Day flight'}</span>
        <span className="ml-auto text-[10px] opacity-60">Cockpit preview · not actual aircraft view</span>
      </div>
    </div>
  );
};

export default CockpitView;
